// src/hooks/useVariants.js
import { useState, useEffect, useMemo } from 'react';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import {
    getVariantsByProductId,
    getVariantsByShopId,
    createVariant,
    updateVariant,
    deleteVariant,
    reserveItem,
    cancelReservation,
    fetchLowStockVariants,
    fetchVariantsByCategory,
    fetchEntireShopVariantCategoryMenuData,
    createVariantCategory,
    updateVariantCategoryWithCascade,
    softDeleteVariantCategoryWithCascade,
    saveVariantCategoriesListOrder,
    fetchVariantsCategoriesByShopId,
    fetchRandomNonElectronicsProducts, // NEW
} from '../store/services/variantService';
// Part 1: Zustand Store (for UI State ONLY)
export const useVariantStore = create(
    persist(
        (set) => ({
            variantList: [],
            selectedVariant: null,

            setVariantList: (variants) => {
                set({ variantList: variants });
            },

            appendVariantList: (newVariants) => {
                set((state) => ({
                    variantList: [...state.variantList, ...newVariants],
                }));
            },

            setSelectedVariant: (variant) => {
                set({ selectedVariant: variant });
            },
        }),
        {
            name: 'variant-storage',
            storage: createJSONStorage(() => localStorage),
        }
    )
);

// Part 2: React Query Hooks (for Server State)
export const useVariantsByProductId = (productId) => {
    const setVariantList = useVariantStore((s) => s.setVariantList);
    const setSelectedVariant = useVariantStore((s) => s.setSelectedVariant);
    
    return useQuery({
        queryKey: ['variants', productId],
        queryFn: () => getVariantsByProductId(productId),
        enabled: !!productId,
        onSuccess: (data) => {
            setVariantList(data);
            // This is the correct place to set the default selected variant if none is in the store.
            setSelectedVariant(data[0]);
        },
        onError: (error) => {
            console.error("Failed to fetch variants by product ID:", error);
        },
    });
};

export const useVariantsByShop = (shopId) => {
    const setVariantList = useVariantStore((s) => s.setVariantList);
    return useQuery({
        queryKey: ['variants', 'byShop', shopId],
        queryFn: () => getVariantsByShopId(shopId),
        enabled: !!shopId,
        onSuccess: (data) => {
            setVariantList(data);
        },
        onError: (error) => {
            console.error("Failed to fetch variants by shop ID:", error);
        },
    });
};

export const useLowStockVariants = (threshold) => {
    return useQuery({
        queryKey: ['lowStock', threshold],
        queryFn: () => fetchLowStockVariants(threshold),
        enabled: !!threshold,
    });
};

export const useSyncVariantsFromCache = (queryKey) => {
    const queryClient = useQueryClient();
    const setVariantList = useVariantStore((s) => s.setVariantList);
    
    // 🛑 CRITICAL FIX: The sync function now accepts and prioritizes 'dataToSync'
    const sync = (dataToSync) => {
        // 1. Prioritize the data passed directly from the hook (which is guaranteed fresh)
        const variants = dataToSync || queryClient.getQueryData(queryKey);
        
        if (variants) {
            // Use the safe setter from the store
            setVariantList(variants); 
        } else {
            console.log("No data found to sync for key:", queryKey);
        }
    };
    return { sync };
};

export const useCreateVariant = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createVariant,
        onSuccess: (newVariant) => {
            queryClient.invalidateQueries({ queryKey: ['variants', newVariant.product_id] });
            queryClient.invalidateQueries({ queryKey: ['variants', 'byShop', newVariant.shop_id] });
        },
        onError: (error) => {
            console.error('Failed to create variant:', error);
        },
    });
};

export const useUpdateVariant = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, payload }) => updateVariant(id, payload),
        onSuccess: (updatedVariant) => {
            queryClient.invalidateQueries({ queryKey: ['variants', updatedVariant.product_id] });
            queryClient.invalidateQueries({ queryKey: ['variants', 'byShop', updatedVariant.shop_id] });
        },
        onError: (error) => {
            console.error('Failed to update variant:', error);
        },
    });
};

export const useDeleteVariant = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteVariant,
        onSuccess: (deletedId) => {
            queryClient.invalidateQueries({ queryKey: ['variants'] });
            console.log('Variant deleted successfully.');
        },
        onError: (error) => {
            console.error('Failed to delete variant:', error);
        },
    });
};



// NEW HOOK: Paginated fetching of variants by category
export const useVariantsByCategory = (category, pageSize = 15) => {
    // The query key includes the category to ensure a new fetch when the category changes.
    // The key structure will look like ['variants', 'byCategory', 'Laptops']
    return useInfiniteQuery({
        queryKey: ['variants', 'byCategory', category],
        queryFn: ({ pageParam = 0 }) => 
            // pageParam is the offset, passed as `offset` in our service function
            fetchVariantsByCategory(category, pageSize, pageParam),
        
        // This function determines the next starting point (offset) for the next fetch.
        getNextPageParam: (lastPage, allPages) => {
            // Calculate the total number of variants fetched so far
            const totalFetched = allPages.flatMap(page => page).length;
            
            // If the last page returned less than the page size, we're at the end.
            if (lastPage.length < pageSize) {
                return undefined; // No more pages
            }

            // The next offset is the total number of items fetched so far.
            return totalFetched;
        },
        
        enabled: !!category,
    });
};

export const useReserveItem = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: reserveItem,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['variants'] });
            queryClient.invalidateQueries({ queryKey: ['lowStock'] });
            console.log('Item reserved successfully.');
        },
        onError: (error) => {
            console.error('Failed to reserve item:', error);
        },
    });
};

export const useCancelReservation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: cancelReservation,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['variants'] });
            queryClient.invalidateQueries({ queryKey: ['lowStock'] });
            console.log('Reservation canceled successfully.');
        },
        onError: (error) => {
            console.error('Failed to cancel reservation:', error);
        },
    });
};





// ..........................................
// ---------------------------------------------------------
// NEW: variants_categories support for non-electronics collections
// (clothes, shoes, artisan collections, etc.) — plain query/mutation
// hooks only, same shape as the rest of this file. Electronics' static
// category list is untouched.
// ---------------------------------------------------------

export const useVariantsCategoriesByShop = (shopId) => {
    return useQuery({
        queryKey: ['variantsCategories', shopId],
        queryFn: () => fetchVariantsCategoriesByShopId(shopId),
        enabled: !!shopId,
    });
};

export const useCreateVariantCategory = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ shopId, name, imageUrl, sortOrder }) =>
            createVariantCategory(shopId, name, imageUrl, sortOrder),
        onSuccess: (_, { shopId }) => {
            queryClient.invalidateQueries({ queryKey: ['variantsCategories', shopId] });
        },
        onError: (error) => {
            console.error('Failed to create variant category:', error);
        },
    });
};

export const useUpdateVariantCategory = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ shopId, categoryId, oldName, newName, imageUrl }) =>
            updateVariantCategoryWithCascade(shopId, categoryId, oldName, newName, imageUrl),
        onSuccess: (_, { shopId }) => {
            queryClient.invalidateQueries({ queryKey: ['variantsCategories', shopId] });
            queryClient.invalidateQueries({ queryKey: ['variants', 'byShop', shopId] });
        },
        onError: (error) => {
            console.error('Failed to update variant category:', error);
        },
    });
};

export const useSoftDeleteVariantCategory = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ shopId, categoryId, categoryName }) =>
            softDeleteVariantCategoryWithCascade(shopId, categoryId, categoryName),
        onSuccess: (_, { shopId }) => {
            queryClient.invalidateQueries({ queryKey: ['variantsCategories', shopId] });
            queryClient.invalidateQueries({ queryKey: ['variants', 'byShop', shopId] });
        },
        onError: (error) => {
            console.error('Failed to delete variant category:', error);
        },
    });
};

export const useSaveVariantCategoriesListOrder = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (orderedCategories) => saveVariantCategoriesListOrder(orderedCategories),
        onSuccess: (orderedCategories) => {
            const shopId = orderedCategories?.[0]?.shop_id;
            if (shopId) {
                queryClient.invalidateQueries({ queryKey: ['variantsCategories', shopId] });
            }
        },
        onError: (error) => {
            console.error('Failed to save variant category order:', error);
        },
    });
};
// Append to src/hooks/useVariants.js
import { getVariantById, getSimilarVariants } from '../store/services/variantService';

export const useVariantById = (id) => {
    return useQuery({
        queryKey: ['variantById', id],
        queryFn: () => getVariantById(id),
        enabled: !!id,
    });
};

export const useSimilarVariants = (category, excludeId) => {
    return useQuery({
        queryKey: ['similarVariants', category, excludeId],
        queryFn: () => getSimilarVariants({ category, excludeId }),
        enabled: !!category,
        staleTime: 1000 * 60 * 5,
    });
};

// Append to src/hooks/useVariants.js
import { getSimilarVariantsByName } from '../store/services/variantService';

export const useSimilarVariantsByName = (productName, category, excludeId) => {
    return useQuery({
        queryKey: ['similarVariantsByName', productName, category, excludeId],
        queryFn: () => getSimilarVariantsByName({ productName, category, excludeId }),
        enabled: !!productName,
        staleTime: 1000 * 60 * 5,
    });
};


/**
 * Home-page showcase of non-electronics products. A fresh random nonce
 * per component mount keeps this from ever serving a cached selection —
 * every visit to the home page, it re-fetches and re-distributes across
 * shops.
 */
export const useRandomNonElectronicsProducts = (limit = 10) => {
    const nonce = useMemo(() => Date.now(), []);
    return useQuery({
        queryKey: ['randomNonElectronicsProducts', limit, nonce],
        queryFn: () => fetchRandomNonElectronicsProducts(limit),
        staleTime: 0,
        gcTime: 0,
    });
};